import React, { useMemo } from 'react';
import { View, Text, ScrollView } from 'react-native';
import i18n from 'i18n-js';

import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { font as initialFont } from '@styles/fonts';
import { scale as initialScale } from '@components/frame';
import Selectors from '@styles/selectors';
import { connect, useSelector } from 'react-redux';
import { Machine } from '@interfaces/machines';
import ColorWrapper, { WithColor } from '@styles/colors';
import { ColorFunction } from '@styles/colors/types';
import { selectSelectedMachienGroupId, setSelectedMachineGroupId } from '@reducers/v2/planboard';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import SearchInput from './SearchInput';
import Actions from './Actions';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';

interface PlanboardHeaderProps extends WithColor {
  scale?: ScaleFunction;
  font?: typeof initialFont;
  selectedMachine?: Machine;
}

const PlanboardHeader: React.FC<PlanboardHeaderProps> = ({ scale = initialScale, font = initialFont, C }) => {
  const dispatch = useAppDispatch();

  const machineGroups = useSelector<any>((state) => state.machines.machinesGroup);
  const selectedMachineGroup = useSelector(selectSelectedMachienGroupId);

  const filterOptions = useMemo(() => {
    const options = [];

    options.push({
      type: 'all',
      id: null,
      name: i18n.t('All'),
    });

    return [{ type: 'all', id: null, name: i18n.t('All') }].concat(
      (machineGroups as any).map((machineGroup: any) => ({
        type: 'machine',
        id: machineGroup.machineGroupId,
        name: machineGroup.name,
      }))
    );
  }, [machineGroups]);

  return (
    <View
      style={{
        position: 'relative',
        flexDirection: 'row',
        alignItems: 'center',
        paddingLeft: scale(18),
        paddingRight: scale(8),
        paddingVertical: scale(22),
      }}
    >
      <Text style={[font['Title 2'], { color: C('Label/Light/Primary') }]}>{i18n.t('Planboard')}</Text>

      <View
        pointerEvents="box-none"
        style={{
          position: 'absolute',
          left: scale(18),
          right: scale(8),
          alignItems: 'center',
        }}
      >
        <ScrollView
          horizontal={true}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            paddingHorizontal: scale(18),
            justifyContent: 'center',
          }}
          style={{ maxWidth: '100%' }}
        >
          <View
            style={{
              flexDirection: 'row',
              gap: scale(8),
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {filterOptions.map((filter) => (
              <Selectors
                key={`${filter.type}-${filter.id}-${filter.name}`}
                {...{
                  scale,
                  font,
                  selected: filter.id === selectedMachineGroup,
                  variant: 'L',
                  text: i18n.t(filter.name),
                  onPress: () => {
                    trackEvent(TRACKING_EVENTS.PLANBOARD, {
                      action: 'machineFiltered',
                      selectedGroupName: filter.name || '',
                    });
                    dispatch(setSelectedMachineGroupId(filter.id));
                  },
                }}
              />
            ))}
          </View>
        </ScrollView>
      </View>

      <View
        style={{
          marginLeft: 'auto',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: scale(16),
        }}
      >
        <SearchInput font={font} scale={scale} />
        {/* TODO: implement this menu once backend and figma updated */}
        {/*<Actions font={font} scale={scale} />*/}
      </View>
    </View>
  );
};

export default connect(() => ({}))(ColorWrapper(PlanboardHeader));
